import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import AuthShell from "@/components/AuthShell";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";

const FOUNDATION_CITIES = [
  { value: "Sydney", label: "Sydney — live now" },
  { value: "Melbourne", label: "Melbourne — opens Q4 2026" },
  { value: "Brisbane", label: "Brisbane — opens Q4 2026" },
  { value: "Perth", label: "Perth — opens Q4 2026" },
];

export default function JoinNetwork() {
  const navigate = useNavigate();
  const location = useLocation();
  const { joinNetwork } = useAuth();
  const [form, setForm] = useState({
    salon_name: "",
    owner_name: "",
    email: "",
    foundation_city: "Sydney",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await joinNetwork(form);
      toast.success("Welcome to the network.");
      const to = location.state?.from || "/pricing";
      navigate(to, { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || "Signup failed. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      subtitle="Book Away partner network"
      title="Join the network to unlock membership options."
      footerText="Already joined?"
      footerHref="/join-network"
      footerLinkLabel="Use the same email to sign back in"
    >
      <form data-testid="join-network-form" onSubmit={onSubmit} className="space-y-5">
        <div
          data-testid="join-spot-notice"
          className="flex items-start gap-3 rounded-2xl border border-gold/40 bg-gold/10 px-4 py-3"
        >
          <span className="mt-1 h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-gold" />
          <p className="text-[0.78rem] leading-relaxed text-[#2A2724]">
            <span className="font-medium">Final few Foundation spots</span> available at{" "}
            <span className="font-serif italic text-gold">$49/mo</span>. Register your
            interest to lock in the launch special.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="salon_name" className="text-xs text-[#6B6B6B]">Salon name</Label>
          <Input
            id="salon_name"
            data-testid="join-salon-name"
            value={form.salon_name}
            onChange={(e) => update("salon_name", e.target.value)}
            placeholder="Maison Noir"
            required
            className="border-black/10 bg-[#F7F5F2] text-[#2A2724] placeholder:text-[#9a9a9a] focus-visible:ring-gold"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="owner_name" className="text-xs text-[#6B6B6B]">Owner name</Label>
          <Input
            id="owner_name"
            data-testid="join-owner-name"
            value={form.owner_name}
            onChange={(e) => update("owner_name", e.target.value)}
            placeholder="Alex Rivera"
            required
            className="border-black/10 bg-[#F7F5F2] text-[#2A2724] placeholder:text-[#9a9a9a] focus-visible:ring-gold"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="foundation_city" className="text-xs text-[#6B6B6B]">
            Which city are you launching in?
          </Label>
          <Select
            value={form.foundation_city}
            onValueChange={(v) => update("foundation_city", v)}
          >
            <SelectTrigger
              id="foundation_city"
              data-testid="join-city"
              className="border-black/10 bg-[#F7F5F2] text-[#2A2724] focus:ring-gold data-[placeholder]:text-[#9a9a9a]"
            >
              <SelectValue placeholder="Choose your city" />
            </SelectTrigger>
            <SelectContent>
              {FOUNDATION_CITIES.map((c) => (
                <SelectItem
                  key={c.value}
                  value={c.value}
                  data-testid={`join-city-${c.value.toLowerCase()}`}
                >
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="email" className="text-xs text-[#6B6B6B]">Email</Label>
          <Input
            id="email"
            type="email"
            data-testid="join-email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="you@studio.com"
            required
            className="border-black/10 bg-[#F7F5F2] text-[#2A2724] placeholder:text-[#9a9a9a] focus-visible:ring-gold"
          />
        </div>

        {error && (
          <p data-testid="join-error" className="text-sm text-red-600">
            {error}
          </p>
        )}

        <Button
          type="submit"
          data-testid="join-submit"
          disabled={loading}
          className="w-full rounded-full bg-gold py-6 text-sm font-medium text-black hover:bg-[#AA8627]"
        >
          {loading ? "Joining the network…" : "View membership options"}
        </Button>

        <p className="text-xs leading-relaxed text-[#9a9a9a]">
          By joining, you agree to be contacted about your Book Away partner
          membership. We'll never share your details.
        </p>
      </form>
    </AuthShell>
  );
}
